import type { PaymentSuccessResponse } from '~/components/common/modals/payment/PaymentHandlerMixin'

type errorType = 'CONFIGURATION' | 'VALIDATION' | 'SERVER'

interface Integration {
  teardown: () => void;
  paypal: {
    initAuthFlow: () => void;
    closeAuthFlow: () => void;
  };
  deviceData: string;
}

interface CreditCardDetails {
  cardType: 'Visa' | 'Mastercard' | 'Discover' | 'American Express' | 'UnionPay' | 'JCB';
  lastTwo: string;
}

interface PayPalDetails {
  billingAddress: {
    countryCodeAlpha2: string;
    locality: string;
    postalCode: string;
    region: string;
    streetAddress: string;
  },
  email: string;
  firstName: string;
  lastName: string;
  payerId: string;
}

interface Payload {
  nonce: string;
  type: 'CreditCard' | 'PayPalAccount';
  details: CreditCardDetails | PayPalDetails;
}

declare global {
  interface Window {
    braintree: any;
  }
}

export namespace PayPal {
  export type ConfigurationError = {
    type: errorType;
    message: string;
  }

  export type InitAuthFlow = () => Promise<void>
  export type TeardownSetup = () => Promise<void>
}

interface InitOptions {
  config: {
    packageId: string;
    isFreeTrial: boolean;
    environment: string;
    billingAgreement: string;
  }
  success: (res: PaymentSuccessResponse) => void;
  fail?: (error: PayPal.ConfigurationError) => void;
  onEvent?: (event: { name: string }) => void
}

let integration: null | Promise<Integration> | Integration = null
type SubmitPayment = (payload: { nonce: string, deviceData?: string; packageId: string; isFreeTrial: boolean; success: (res: PaymentSuccessResponse) => void }) => Promise<PaymentSuccessResponse>
type InitSetup = (config: InitOptions) => ({ initAuthFlow: PayPal.InitAuthFlow; teardownSetup: PayPal.TeardownSetup })

declare module 'vue/types/vue' {
  interface Vue {
    $initPayPal: InitSetup
  }
}

export default ({ $accessor, $api }, inject) => {
  const getToken = (): Promise<string> => {
    return $accessor.payment.getToken()
  }

  const getIntegration = (): Promise<Integration> => {
    return ((integration as Promise<Integration>).then
      ? integration
      : Promise.resolve(integration)) as Promise<Integration>
  }

  const initAuthFlow: PayPal.InitAuthFlow = () => {
    return getIntegration().then((integration) => {
      integration.paypal.initAuthFlow()
    })
  }

  const teardownSetup: PayPal.TeardownSetup = () => {
    return getIntegration().then((integration) => {
      integration.teardown()
      integration = null
    })
  }

  const submitPayment: SubmitPayment = ({ nonce, deviceData, packageId, isFreeTrial, success }) => {
    const form = new FormData()

    form.append('package_id', packageId)
    form.append('free_trial', isFreeTrial.toString())
    form.append('payment_method_nonce', nonce)

    if (deviceData) {
      form.append('device_data', deviceData)
    }

    return $api.payment.sendTokenizedData(form)
      .then(res => success(res.data.data))
  }

  const initSetup: InitSetup = ({ config: { packageId, isFreeTrial, billingAgreement, environment }, success, fail, onEvent }) => {
    integration = new Promise((resolve, reject) => {
      getToken().then((token) => {
        window.braintree.setup(token, 'custom', {
          onReady: (instance: Integration) => {
            integration = instance
            resolve(instance)
          },
          onError: (error: PayPal.ConfigurationError) => {
            fail(error)
            reject(error)
          },
          onPaymentMethodReceived: (payload: Payload) => {
            onEvent({ name: 'before_submit' })
            submitPayment({
              nonce: payload.nonce,
              deviceData: (integration as Integration).deviceData,
              packageId,
              isFreeTrial,
              success
            }).catch((err) => {
              fail({
                type: err.response.data.statusText,
                message: err.response.data.error
              })
            })
          },
          dataCollector: { kount: { environment } },
          paypal: {
            headless: true,
            singleUse: false,
            billingAgreementDescription: billingAgreement
          }
        })
      })
    })

    return { initAuthFlow, teardownSetup }
  }

  inject('initPayPal', initSetup)
}
